import apiConfig from 'src/integration/config/apiConfig';
import useQueryKey from 'src/toolkit/utils/useQueryKey';
import { useStore } from 'src/integration/store/store';
import useReactQueryJava from 'src/integration/api/useReactQueryJava';
import { MIN_15 } from 'src/integration/config/constants';
import colors from 'src/integration/styles/colors';

interface FfrCapacityVolumesDto {
  id: number;
  date: string;
  time: string;
  mfrrUpCapacity: {
    value: number;
    unit: string;
  } | null;
  mfrrDowncapacity: {
    value: number;
    unit: string;
  } | null;
  afrrUpCapacity: {
    value: number;
    unit: string;
  } | null;
  afrrDownCapacity: {
    value: number;
    unit: string;
  } | null;
  fcrCapacity: {
    value: number;
    unit: string;
  } | null;
  fcrActivation: {
    value: number;
    unit: string;
  } | null;
}

const emptyChartData = {
  labels: [],
  lineLabels: [],
  alignDualXAxis: true,
  datasets: [
    {
      label: 'mFRR up capacity',
      data: [],
      backgroundColor: colors.chart.carminePink,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
    {
      label: 'mFRR down capacity',
      data: [],
      backgroundColor: colors.chart.carminePinkLight,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
    {
      label: 'aFRR up capacity',
      data: [],
      backgroundColor: colors.chart.pastelOrange,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
    {
      label: 'aFRR down capacity',
      data: [],
      backgroundColor: colors.chart.pastelOrangeLight,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
    {
      label: 'FCR capacity',
      data: [],
      backgroundColor: colors.chart.oldMauve,
      borderRadius: 4,
      type: 'bar',
      xAxisID: 'x',
    },
  ],
};

const useFetchFfrCapacityVolumes = () => {
  const { settingsStore } = useStore();
  const { selectedSiteId, selectedDate } = settingsStore;

  const response = useReactQueryJava<FfrCapacityVolumesDto[], SimpleObject>({
    endpoint: apiConfig.api.endpointsJava.ffrCapacityVolumes,
    queryKey: useQueryKey('ffrCapacityVolumes', [selectedDate, selectedSiteId?.toString()]),
    method: 'GET',
    queryParams: {
      date: selectedDate,
      siteId: selectedSiteId,
    },
    opts: {
      enabled: !!selectedSiteId,
      refetchOnWindowFocus: false,
      refetchInterval: MIN_15,
    },
  });

  const chartData = response?.data ? {
    labels: response.data.map((item) => item.time),
    lineLabels: response.data.map((item) => item.time),
    alignDualXAxis: true,
    datasets: [
      {
        ...emptyChartData.datasets[0],
        data: response.data.map((item) => ({ x: item.time, y: item.mfrrUpCapacity?.value ?? 0 })),
      },
      {
        ...emptyChartData.datasets[1],
        data: response.data.map((item) => ({ x: item.time, y: item.mfrrDowncapacity?.value ?? 0 })),
      },
      {
        ...emptyChartData.datasets[2],
        data: response.data.map((item) => ({ x: item.time, y: item.afrrUpCapacity?.value ?? 0 })),
      },
      {
        ...emptyChartData.datasets[3],
        data: response.data.map((item) => ({ x: item.time, y: item.afrrDownCapacity?.value ?? 0 })),
      },
      {
        ...emptyChartData.datasets[4],
        data: response.data.map((item) => ({ x: item.time, y: item.fcrCapacity?.value ?? 0 })),
      },
    ],
  } : emptyChartData;

  return {
    ...response,
    chartData,
  };
};

export default useFetchFfrCapacityVolumes;
